import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { fetchUsers, createUser, updateUser, deleteUser, fetchAuditLogs, fetchOrdini, fetchGTL, fetchPendingProposals, handleProposal, validateBulkUsers, importBulkUsers, exportUsersCSV } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Users, History, UserPlus, Shield, Eye, Edit, Building2, FolderKanban, CheckCircle, XCircle, Trash2, ArrowUpDown, ArrowUp, ArrowDown, Search, ChevronLeft, ChevronRight, Upload, Download, FileText, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export default function ImpostazioniPage() {
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [ordini, setOrdini] = useState([]);
  const [gtlList, setGtlList] = useState([]);
  const [proposte, setProposte] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingProposte, setLoadingProposte] = useState(false);
  
  // Bulk import state
  const [bulkImportModal, setBulkImportModal] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [validationResults, setValidationResults] = useState(null);
  const [validating, setValidating] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  
  // Pagination and search state
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const usersPerPage = 20;
  
  const [newUser, setNewUser] = useState({ 
    email: '', 
    password: '', 
    nome: '', 
    cognome: '',
    cellulare: '',
    ruolo_con_accesso: '',  // AMMINISTRATORE, ADMIN_ORDINE, CONSIGLIERE_C3I, COORDINATORE_GTL
    ruoli_senza_accesso: [],  // Array of: DELEGATO_C3I, DELEGATO_SUPPLENTE, etc.
    ordine_appartenenza_id: '',
    gtl_id: '',
    gtl_ids: []
  });
  const [createUserModal, setCreateUserModal] = useState(false);
  const [editUserModal, setEditUserModal] = useState(false);
  const [deleteUserConfirm, setDeleteUserConfirm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  const { isAmministratore, isAdminOrdine, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAmministratore() && !isAdminOrdine()) {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [navigate, isAmministratore, isAdminOrdine]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load users, ordini, and GTL for all roles
      const promises = [
        fetchUsers(),
        fetchOrdini({ limit: 200 }),
        fetchGTL({ limit: 100 })
      ];
      
      // Only load audit logs for Amministratore
      if (isAmministratore()) {
        promises.push(fetchAuditLogs({ limit: 50 }));
      }
      
      const results = await Promise.all(promises);
      
      setUsers(results[0]);
      setOrdini(results[1].items);
      
      // Sort GTL alphabetically by name
      const sortedGTL = (results[2].items || []).sort((a, b) => 
        a.nome.localeCompare(b.nome, 'it', { sensitivity: 'base' })
      );
      setGtlList(sortedGTL);
      
      if (isAmministratore() && results[3]) {
        setAuditLogs(results[3].items);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProposte = async () => {
    setLoadingProposte(true);
    try {
      const data = await fetchPendingProposals();
      setProposte(data);
    } catch (error) {
      console.error('Error loading proposte:', error);
    } finally {
      setLoadingProposte(false);
    }
  };

  const handleApproveProposal = async (propostaId) => {
    try {
      await handleProposal(propostaId, 'approva');
      toast.success('Proposta approvata! Membro aggiunto al GTL');
      loadProposte();
    } catch (error) {
      console.error('Error approving proposal:', error);
      toast.error(error.response?.data?.detail || 'Errore durante l\'approvazione');
    }
  };

  const handleRejectProposal = async (propostaId, motivo = 'Non approvato') => {
    try {
      await handleProposal(propostaId, 'rifiuta', motivo);
      toast.success('Proposta rifiutata');
      loadProposte();
    } catch (error) {
      console.error('Error rejecting proposal:', error);
      toast.error(error.response?.data?.detail || 'Errore durante il rifiuto');
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.nome || !newUser.cognome || !newUser.cellulare) {
      toast.error('Nome, cognome, email e cellulare sono obbligatori');
      return;
    }
    
    // Admin Ordine: auto-set ordine_appartenenza_id, no role selection needed
    const ordineId = isAdminOrdine() ? user.ordine_appartenenza_id : newUser.ordine_appartenenza_id;
    
    if (!ordineId) {
      toast.error('Seleziona l\'ordine di appartenenza (obbligatorio)');
      return;
    }
    
    // For Amministratore only: validate ruolo_con_accesso requirements
    if (!isAdminOrdine()) {
      if (newUser.ruolo_con_accesso && !newUser.password) {
        toast.error('La password è obbligatoria per utenti con accesso alla piattaforma');
        return;
      }
      if (!newUser.ruolo_con_accesso && newUser.ruoli_senza_accesso.length === 0) {
        toast.error('Seleziona almeno un ruolo per l\'utente');
        return;
      }
      if (newUser.ruolo_con_accesso === 'COORDINATORE_GTL' && !newUser.gtl_id) {
        toast.error('Seleziona il GTL da coordinare');
        return;
      }
    }
    
    setSaving(true);
    try {
      await createUser({
        ...newUser,
        ordine_appartenenza_id: ordineId
      });
      toast.success(isAdminOrdine() ? 'Iscritto aggiunto con successo' : 'Utente creato con successo');
      setCreateUserModal(false);
      setNewUser({ 
        email: '', password: '', nome: '', cognome: '', cellulare: '',
        ruolo_con_accesso: '', ruoli_senza_accesso: [], 
        ordine_appartenenza_id: '', gtl_id: '' 
      });
      loadData();
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.detail || 'Errore durante la creazione');
    } finally {
      setSaving(false);
    }
  };

  const handleEditUser = async () => {
    // Admin Ordine: can only update email and cellulare
    if (isAdminOrdine()) {
      setSaving(true);
      try {
        await updateUser(selectedUser.id, {
          email: selectedUser.email,
          cellulare: selectedUser.cellulare
        });
        toast.success('Iscritto aggiornato con successo');
        setEditUserModal(false);
        setSelectedUser(null);
        loadData();
      } catch (error) {
        console.error('Error updating user:', error);
        toast.error(error.response?.data?.detail || 'Errore durante l\'aggiornamento');
      } finally {
        setSaving(false);
      }
      return;
    }
    
    // Amministratore: full validation
    if (!selectedUser.nome || !selectedUser.cognome || !selectedUser.cellulare) {
      toast.error('Nome, cognome e cellulare sono obbligatori');
      return;
    }
    if (!selectedUser.ordine_appartenenza_id) {
      toast.error('Seleziona l\'ordine di appartenenza');
      return;
    }
    if (!selectedUser.ruolo_con_accesso && selectedUser.ruoli_senza_accesso.length === 0) {
      toast.error('Seleziona almeno un ruolo per l\'utente');
      return;
    }
    if (selectedUser.ruolo_con_accesso === 'COORDINATORE_GTL' && !selectedUser.gtl_id) {
      toast.error('Seleziona il GTL da coordinare');
      return;
    }
    setSaving(true);
    try {
      await updateUser(selectedUser.id, selectedUser);
      toast.success('Utente aggiornato con successo');
      setEditUserModal(false);
      setSelectedUser(null);
      loadData();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.detail || 'Errore durante l\'aggiornamento');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    setDeleting(true);
    try {
      await deleteUser(selectedUser.id);
      toast.success('Utente eliminato con successo');
      setDeleteUserConfirm(false);
      setSelectedUser(null);
      loadData();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Errore durante l\'eliminazione');
    } finally {
      setDeleting(false);
    }
  };

  // Bulk Import Functions
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Seleziona un file CSV');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n');
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/ /g, '_'));
      
      const data = [];
      for (let i = 1; i < lines.length; i++) {
        if (!lines[i].trim()) continue;
        
        const values = parseCSVLine(lines[i]);
        const row = {};
        headers.forEach((header, idx) => {
          row[header] = values[idx] || '';
        });
        data.push(row);
      }
      
      setCsvData(data);
      setValidationResults(null);
      setBulkImportModal(true);
    };
    reader.readAsText(file);
    
    // Reset file input
    event.target.value = '';
  };
  
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };
  
  const handleValidateCSV = async () => {
    if (csvData.length === 0) {
      toast.error('Nessun dato da validare');
      return;
    }
    
    setValidating(true);
    try {
      const results = await validateBulkUsers(csvData);
      setValidationResults(results);
      
      if (results.errors === 0) {
        toast.success(`Validazione completata: ${results.valid} utenti pronti per l'importazione`);
      } else {
        toast.warning(`Validazione completata: ${results.valid} validi, ${results.errors} errori`);
      }
    } catch (error) {
      console.error('Validation error:', error);
      toast.error(error.response?.data?.detail || 'Errore durante la validazione');
    } finally {
      setValidating(false);
    }
  };
  
  const handleImportCSV = async () => {
    if (!validationResults || validationResults.errors > 0) {
      toast.error('Correggi gli errori prima di importare');
      return;
    }
    
    setImporting(true);
    try {
      const result = await importBulkUsers(csvData);
      toast.success(result.message);
      setBulkImportModal(false);
      setCsvData([]);
      setValidationResults(null);
      loadData();
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Errore durante l\'importazione');
    } finally {
      setImporting(false);
    }
  };
  
  const handleExportUsersTemplate = () => {
    exportUsersCSV();
  };

  const getRoleBadge = (ruolo) => {
    switch (ruolo) {
      case 'AMMINISTRATORE':
        return <span className="badge-success">Amministratore</span>;
      case 'ADMIN_ORDINE':
        return <span className="badge-warning">Admin Ordine</span>;
      case 'CONSIGLIERE_C3I':
        return <span className="badge-neutral">Consigliere C3i</span>;
      case 'COORDINATORE_GTL':
        return <span className="bg-blue-100 text-blue-700 border border-blue-200 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold">Coordinatore GTL</span>;
      default:
        return <span className="badge-neutral">{ruolo}</span>;
    }
  };

  const getRoleIcon = (ruolo) => {
    switch (ruolo) {
      case 'SEGRETARIO_C3I':
        return <Shield className="w-4 h-4 text-emerald-600" />;
      case 'ADMIN_ORDINE':
        return <Edit className="w-4 h-4 text-amber-600" />;
      case 'COORDINATORE_GTL':
        return <FolderKanban className="w-4 h-4 text-blue-600" />;
      default:
        return <Eye className="w-4 h-4 text-slate-400" />;
    }
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleString('it-IT');
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8">
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="impostazioni-page">
      {/* Header */}
      <div className="page-header -mx-6 lg:-mx-8 -mt-6 lg:-mt-8 mb-8 px-6 lg:px-8 py-6">
        <h1 className="font-heading text-2xl font-semibold text-slate-900">
          {isAdminOrdine() ? 'Gestione iscritti' : 'Gestione'}
        </h1>
        <p className="text-slate-500 mt-1">
          {isAmministratore() 
            ? 'Utenti e Log di sistema' 
            : 'Gestione degli iscritti del tuo ordine'}
        </p>
      </div>

      <Tabs defaultValue="users" className="space-y-6">
        {/* Show tabs only for Amministratore */}
        {isAmministratore() && (
          <TabsList className="bg-slate-100">
            <TabsTrigger value="users" className="data-[state=active]:bg-white" data-testid="tab-users">
              <Users className="w-4 h-4 mr-2" />
              Utenti
            </TabsTrigger>
            <TabsTrigger value="audit" className="data-[state=active]:bg-white" data-testid="tab-audit">
              <History className="w-4 h-4 mr-2" />
              Log Attività
            </TabsTrigger>
          </TabsList>
        )}

        {/* Users Tab */}
        <TabsContent value="users">
          <Card className="card-institutional">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg">
                {isAdminOrdine() ? 'Iscritti del tuo Ordine con ruolo nel C3i' : 'Utenti'}
              </CardTitle>
              <div className="flex gap-2">
                {isAmministratore() && (
                  <>
                    <Button variant="outline" onClick={handleExportUsersTemplate} data-testid="export-users-btn">
                      <Download className="w-4 h-4 mr-2" />
                      Esporta CSV
                    </Button>
                    <input
                      type="file"
                      accept=".csv"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <Button variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="import-users-btn">
                      <Upload className="w-4 h-4 mr-2" />
                      Importa CSV
                    </Button>
                    <Button onClick={() => setCreateUserModal(true)} data-testid="create-user-btn">
                      <UserPlus className="w-4 h-4 mr-2" />
                      Nuovo Utente
                    </Button>
                  </>
                )}
                {isAdminOrdine() && (
                  <Button onClick={() => setCreateUserModal(true)} data-testid="create-iscritto-btn">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Aggiungi Iscritto
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {/* Search bar for Amministratore */}
              {isAmministratore() && (
                <div className="p-4 border-b">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <Input
                      type="text"
                      placeholder="Cerca per cognome o ordine"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="pl-10"
                    />
                  </div>
                </div>
              )}
              
              {(() => {
                // Filter users for Admin Ordine: show only users with DELEGA or GTL roles
                let filteredUsers = isAdminOrdine() 
                  ? users.filter(u => {
                      const hasDelegaRole = u.ruoli_senza_accesso?.includes('DELEGATO_C3I') || 
                                           u.ruoli_senza_accesso?.includes('DELEGATO_SUPPLENTE');
                      const hasGTLRole = u.ruoli_senza_accesso?.some(r => 
                        ['MEMBRO_GTL', 'MEMBRO_SPECIALE_GTL', 'REFERENTE_GTL'].includes(r)
                      );
                      return hasDelegaRole || hasGTLRole;
                    })
                  : users;
                
                // Search filter (for Amministratore only)
                if (isAmministratore() && searchQuery.trim()) {
                  const query = searchQuery.toLowerCase();
                  filteredUsers = filteredUsers.filter(u => {
                    const cognome = (u.cognome || '').toLowerCase();
                    const userOrdine = ordini.find(o => o.id === u.ordine_appartenenza_id);
                    const ordineText = userOrdine ? 
                      `${userOrdine.denominazione} ${userOrdine.sigla_provincia}`.toLowerCase() : '';
                    return cognome.includes(query) || ordineText.includes(query);
                  });
                }
                
                // Sorting
                filteredUsers = [...filteredUsers].sort((a, b) => {
                  let aValue, bValue;
                  
                  switch(sortField) {
                    case 'cognome':
                      aValue = a.cognome || '';
                      bValue = b.cognome || '';
                      break;
                    case 'ordine':
                      const aOrdine = ordini.find(o => o.id === a.ordine_appartenenza_id);
                      const bOrdine = ordini.find(o => o.id === b.ordine_appartenenza_id);
                      aValue = aOrdine?.sigla_provincia || '';
                      bValue = bOrdine?.sigla_provincia || '';
                      break;
                    case 'ruolo_con_accesso':
                      aValue = a.ruolo_con_accesso || '';
                      bValue = b.ruolo_con_accesso || '';
                      break;
                    case 'ruolo_delega':
                      aValue = a.ruoli_senza_accesso?.includes('DELEGATO_C3I') ? 'A' : 
                               a.ruoli_senza_accesso?.includes('DELEGATO_SUPPLENTE') ? 'B' : 'Z';
                      bValue = b.ruoli_senza_accesso?.includes('DELEGATO_C3I') ? 'A' : 
                               b.ruoli_senza_accesso?.includes('DELEGATO_SUPPLENTE') ? 'B' : 'Z';
                      break;
                    case 'ruolo_gtl':
                      aValue = a.ruoli_senza_accesso?.some(r => ['MEMBRO_GTL', 'MEMBRO_SPECIALE_GTL', 'REFERENTE_GTL'].includes(r)) ? 'A' : 'Z';
                      bValue = b.ruoli_senza_accesso?.some(r => ['MEMBRO_GTL', 'MEMBRO_SPECIALE_GTL', 'REFERENTE_GTL'].includes(r)) ? 'A' : 'Z';
                      break;
                    case 'created_at':
                    default:
                      aValue = a.created_at || '';
                      bValue = b.created_at || '';
                  }
                  
                  if (sortDirection === 'asc') {
                    return aValue > bValue ? 1 : -1;
                  } else {
                    return aValue < bValue ? 1 : -1;
                  }
                });
                
                // For Admin Ordine: priority sorting (Delegato > Supplente > alphabetical)
                if (isAdminOrdine()) {
                  filteredUsers.sort((a, b) => {
                    const aDelegato = a.ruoli_senza_accesso?.includes('DELEGATO_C3I');
                    const bDelegato = b.ruoli_senza_accesso?.includes('DELEGATO_C3I');
                    const aSupplente = a.ruoli_senza_accesso?.includes('DELEGATO_SUPPLENTE');
                    const bSupplente = b.ruoli_senza_accesso?.includes('DELEGATO_SUPPLENTE');
                    
                    if (aDelegato && !bDelegato) return -1;
                    if (!aDelegato && bDelegato) return 1;
                    if (aSupplente && !bSupplente) return -1;
                    if (!aSupplente && bSupplente) return 1;
                    
                    return a.cognome.localeCompare(b.cognome, 'it', { sensitivity: 'base' });
                  });
                }
                
                // Pagination - ALWAYS apply for Amministratore
                const totalFilteredUsers = filteredUsers.length;
                const totalPages = Math.ceil(totalFilteredUsers / usersPerPage);
                const startIndex = (currentPage - 1) * usersPerPage;
                const paginatedUsers = isAmministratore() 
                  ? filteredUsers.slice(startIndex, startIndex + usersPerPage)
                  : filteredUsers;
                
                const handleSort = (field) => {
                  if (sortField === field) {
                    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                  } else {
                    setSortField(field);
                    setSortDirection('asc');
                  }
                  setCurrentPage(1);
                };
                
                const SortIcon = ({ field }) => {
                  if (sortField !== field) return <ArrowUpDown className="w-4 h-4 ml-1 opacity-30" />;
                  return sortDirection === 'asc' ? 
                    <ArrowUp className="w-4 h-4 ml-1" /> : 
                    <ArrowDown className="w-4 h-4 ml-1" />;
                };
                
                return (
                  <>
                    {paginatedUsers.length === 0 ? (
                      <div className="empty-state py-12">
                        <Users className="empty-state-icon" />
                        <p className="empty-state-title">Nessun risultato</p>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="data-table" data-testid="users-table">
                          <thead>
                            <tr>
                              <th className="cursor-pointer" onClick={() => isAmministratore() && handleSort('cognome')}>
                                <div className="flex items-center">
                                  Iscritto
                                  {isAmministratore() && <SortIcon field="cognome" />}
                                </div>
                              </th>
                              {isAmministratore() && (
                                <th className="cursor-pointer" onClick={() => handleSort('ordine')}>
                                  <div className="flex items-center">
                                    Ordine
                                    <SortIcon field="ordine" />
                                  </div>
                                </th>
                              )}
                              {isAmministratore() && (
                                <th className="cursor-pointer" onClick={() => handleSort('ruolo_con_accesso')}>
                                  <div className="flex items-center">
                                    Ruolo con Accesso
                                    <SortIcon field="ruolo_con_accesso" />
                                  </div>
                                </th>
                              )}
                              <th className="cursor-pointer" onClick={() => isAmministratore() && handleSort('ruolo_delega')}>
                                <div className="flex items-center">
                                  Ruolo Delega
                                  {isAmministratore() && <SortIcon field="ruolo_delega" />}
                                </div>
                              </th>
                              <th className="cursor-pointer" onClick={() => isAmministratore() && handleSort('ruolo_gtl')}>
                                <div className="flex items-center">
                                  Ruolo GTL
                                  {isAmministratore() && <SortIcon field="ruolo_gtl" />}
                                </div>
                              </th>
                              <th>Email</th>
                          <th>Cellulare</th>
                          <th className="w-24">Azioni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedUsers.map((user) => {
                          const userOrdine = ordini.find(o => o.id === user.ordine_appartenenza_id);
                          const isDelegatoC3i = user.ruoli_senza_accesso?.includes('DELEGATO_C3I');
                          const isDelegatoSupplente = user.ruoli_senza_accesso?.includes('DELEGATO_SUPPLENTE');
                          
                          return (
                            <tr key={user.id} data-testid={`user-row-${user.id}`}>
                              <td>
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center text-xs font-semibold text-slate-600">
                                    {user.nome?.charAt(0)}{user.cognome?.charAt(0)}
                                  </div>
                                  <span className="font-medium text-slate-900">{user.nome} {user.cognome}</span>
                                </div>
                              </td>
                              {isAmministratore() && (
                                <td>
                                  {userOrdine ? (
                                    <div className="flex items-center gap-2">
                                      <Building2 className="w-4 h-4 text-slate-400" />
                                      <span className="text-sm">{userOrdine.sigla_provincia}</span>
                                    </div>
                                  ) : (
                                    <span className="text-slate-400 text-sm">-</span>
                                  )}
                                </td>
                              )}
                              {isAmministratore() && (
                                <td>
                                  {user.ruolo_con_accesso ? (
                                    <div>
                                      {getRoleBadge(user.ruolo_con_accesso)}
                                      {user.ruolo_con_accesso === 'COORDINATORE_GTL' && user.gtl_id && (
                                        <div className="text-xs text-slate-500 mt-1">
                                          {gtlList.find(g => g.id === user.gtl_id)?.nome || 'GTL non trovato'}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-xs text-slate-400">Nessuno</span>
                                  )}
                                </td>
                              )}
                              <td>
                                {isDelegatoC3i && (
                                  <span className="badge-success">Delegato C3i</span>
                                )}
                                {isDelegatoSupplente && (
                                  <span className="badge-warning">Delegato Supplente</span>
                                )}
                                {!isDelegatoC3i && !isDelegatoSupplente && (
                                  <span className="text-xs text-slate-400">Nessuno</span>
                                )}
                              </td>
                              <td>
                                {(() => {
                                  const hasMembroGTL = user.ruoli_senza_accesso?.includes('MEMBRO_GTL');
                                  const hasMembroSpeciale = user.ruoli_senza_accesso?.includes('MEMBRO_SPECIALE_GTL');
                                  const hasReferente = user.ruoli_senza_accesso?.includes('REFERENTE_GTL');
                                  const gtlIds = user.gtl_ids || [];
                                  
                                  if (hasMembroGTL || hasMembroSpeciale || hasReferente) {
                                    const roleName = hasMembroGTL ? 'Membro' : hasMembroSpeciale ? 'Membro Speciale' : 'Referente';
                                    const badgeClass = hasMembroGTL ? 'bg-blue-100 text-blue-700 border-blue-200' : 
                                                      hasMembroSpeciale ? 'bg-purple-100 text-purple-700 border-purple-200' : 
                                                      'bg-indigo-100 text-indigo-700 border-indigo-200';
                                    
                                    if (gtlIds.length > 0) {
                                      // Show role with count of GTLs - sorted alphabetically
                                      const gtlNames = gtlIds
                                        .map(id => {
                                          const gtl = gtlList.find(g => g.id === id);
                                          return gtl ? gtl.nome : id;
                                        })
                                        .sort((a, b) => a.localeCompare(b, 'it', { sensitivity: 'base' }));
                                      
                                      return (
                                        <div className="flex flex-col gap-1">
                                          <span className={`${badgeClass} border inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold`}>
                                            {roleName} ({gtlIds.length} GTL)
                                          </span>
                                          <span className="text-xs text-slate-500" title={gtlNames.join(', ')}>
                                            {gtlNames.slice(0, 2).join(', ')}
                                            {gtlNames.length > 2 && ` +${gtlNames.length - 2}`}
                                          </span>
                                        </div>
                                      );
                                    } else {
                                      return (
                                        <span className={`${badgeClass} border inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold`}>
                                          {roleName} GTL
                                        </span>
                                      );
                                    }
                                  }
                                  return <span className="text-xs text-slate-400">Nessuno</span>;
                                })()}
                              </td>
                              <td className="text-sm">
                                {user.email ? (
                                  <a href={`mailto:${user.email}`} className="text-blue-600 hover:underline">
                                    {user.email}
                                  </a>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </td>
                              <td className="text-sm text-slate-600">
                                {user.cellulare ? (
                                  <a href={`tel:${user.cellulare}`} className="hover:text-slate-900">
                                    {user.cellulare}
                                  </a>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </td>
                              <td>
                                <div className="flex gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => { setSelectedUser({...user}); setEditUserModal(true); }}
                                    data-testid={`edit-user-${user.id}`}
                                    title={isAdminOrdine() ? "Modifica iscritto" : "Modifica"}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  {isAmministratore() && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                      onClick={() => { setSelectedUser(user); setDeleteUserConfirm(true); }}
                                      data-testid={`delete-user-${user.id}`}
                                      title="Elimina"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                    )}
                  </>
                );
              })()}
              
              {/* Pagination for Amministratore */}
              {isAmministratore() && users.length > 0 && (() => {
                const filteredCount = searchQuery.trim() ? 
                  users.filter(u => {
                    const query = searchQuery.toLowerCase();
                    const cognome = (u.cognome || '').toLowerCase();
                    const userOrdine = ordini.find(o => o.id === u.ordine_appartenenza_id);
                    const ordineText = userOrdine ? 
                      `${userOrdine.denominazione} ${userOrdine.sigla_provincia}`.toLowerCase() : '';
                    return cognome.includes(query) || ordineText.includes(query);
                  }).length : users.length;
                const totalPages = Math.ceil(filteredCount / usersPerPage);
                
                if (totalPages <= 1) return null;
                
                return (
                  <div className="p-4 border-t flex flex-col items-center">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft className="w-4 h-4 mr-1" />
                        Precedente
                      </Button>
                      <span className="text-sm text-slate-600 px-2">
                        {currentPage} / {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Successiva
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                    <div className="text-sm text-slate-500 mt-2">
                      Pagina {currentPage} di {totalPages} ({filteredCount} utenti)
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Proposte GTL Tab */}
        <TabsContent value="proposte">
          <Card className="card-institutional">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Proposte Membri GTL</CardTitle>
              <p className="text-sm text-slate-500 mt-1">
                Richieste di aggiunta membri da parte degli Admin Ordine
              </p>
            </CardHeader>
            <CardContent className="p-0">
              {loadingProposte ? (
                <div className="p-8 text-center">
                  <div className="spinner mx-auto" />
                </div>
              ) : proposte.length === 0 ? (
                <div className="empty-state py-12">
                  <FolderKanban className="empty-state-icon" />
                  <p className="empty-state-title">Nessuna proposta in attesa</p>
                  <p className="empty-state-description">
                    Le proposte di nuovi membri GTL appariranno qui
                  </p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="data-table" data-testid="proposte-table">
                    <thead>
                      <tr>
                        <th>GTL</th>
                        <th>Persona Proposta</th>
                        <th>Ruolo</th>
                        <th>Proposta da</th>
                        <th>Data</th>
                        <th className="w-32">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {proposte.map((proposta) => (
                        <tr key={proposta.id} data-testid={`proposta-row-${proposta.id}`}>
                          <td>
                            <div className="flex items-center gap-2">
                              <FolderKanban className="w-4 h-4 text-blue-600" />
                              <span className="font-medium text-slate-900">{proposta.gtl?.nome}</span>
                            </div>
                          </td>
                          <td>
                            <div>
                              <p className="font-medium text-slate-900">
                                {proposta.persona?.titolo} {proposta.persona?.nome} {proposta.persona?.cognome}
                              </p>
                              <p className="text-xs text-slate-500">{proposta.persona?.email}</p>
                            </div>
                          </td>
                          <td>
                            {proposta.ruolo_nel_gtl === 'coordinatore' ? (
                              <span className="badge-success">Coordinatore</span>
                            ) : (
                              <span className="badge-neutral">Membro</span>
                            )}
                          </td>
                          <td>
                            <div>
                              <p className="text-sm text-slate-900">
                                {proposta.proposto_da?.nome} {proposta.proposto_da?.cognome}
                              </p>
                              <p className="text-xs text-slate-500">{proposta.proposto_da?.email}</p>
                            </div>
                          </td>
                          <td className="text-sm text-slate-500">{formatDate(proposta.created_at)}</td>
                          <td>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={() => handleApproveProposal(proposta.id)}
                                data-testid={`approve-${proposta.id}`}
                                title="Approva"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleRejectProposal(proposta.id)}
                                data-testid={`reject-${proposta.id}`}
                                title="Rifiuta"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Tab */}
        <TabsContent value="audit">
          <Card className="card-institutional">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Log delle Attività</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {auditLogs.length === 0 ? (
                <div className="empty-state py-12">
                  <History className="empty-state-icon" />
                  <p className="empty-state-title">Nessuna attività registrata</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="data-table" data-testid="audit-table">
                    <thead>
                      <tr>
                        <th>Data/Ora</th>
                        <th>Utente</th>
                        <th>Azione</th>
                        <th>Entità</th>
                        <th>Dettagli</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditLogs.map((log) => (
                        <tr key={log.id}>
                          <td className="text-sm">{formatDate(log.timestamp)}</td>
                          <td className="text-sm">{log.user_email}</td>
                          <td>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              log.action === 'CREATE' ? 'bg-emerald-100 text-emerald-700' :
                              log.action === 'UPDATE' ? 'bg-blue-100 text-blue-700' :
                              log.action === 'DELETE' ? 'bg-red-100 text-red-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {log.action}
                            </span>
                          </td>
                          <td className="text-sm">{log.entity_type}</td>
                          <td className="text-sm text-slate-500 max-w-xs truncate">{log.details || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create User Modal */}
      <Dialog open={createUserModal} onOpenChange={setCreateUserModal}>
        <DialogContent data-testid="create-user-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">Nuovo Utente</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nome *</Label>
                <Input
                  value={newUser.nome}
                  onChange={(e) => setNewUser({ ...newUser, nome: e.target.value })}
                  placeholder="Mario"
                  data-testid="new-user-nome"
                />
              </div>
              <div className="space-y-2">
                <Label>Cognome *</Label>
                <Input
                  value={newUser.cognome}
                  onChange={(e) => setNewUser({ ...newUser, cognome: e.target.value })}
                  placeholder="Rossi"
                  data-testid="new-user-cognome"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="email@esempio.it"
                data-testid="new-user-email"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Cellulare *</Label>
              <Input
                type="tel"
                value={newUser.cellulare}
                onChange={(e) => setNewUser({ ...newUser, cellulare: e.target.value })}
                placeholder="+39 333 1234567"
                data-testid="new-user-cellulare"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Ordine di Appartenenza *</Label>
              {isAdminOrdine() ? (
                <div className="p-3 bg-slate-100 rounded-md border border-slate-200">
                  <p className="text-sm text-slate-700">
                    {ordini.find(o => o.id === user.ordine_appartenenza_id)?.denominazione || 'Il tuo ordine'}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Puoi creare utenti solo per il tuo ordine
                  </p>
                </div>
              ) : (
                <Select 
                  value={newUser.ordine_appartenenza_id} 
                  onValueChange={(v) => setNewUser({ ...newUser, ordine_appartenenza_id: v })}
                >
                  <SelectTrigger data-testid="new-user-ordine-appartenenza">
                    <SelectValue placeholder="Seleziona ordine..." />
                  </SelectTrigger>
                  <SelectContent>
                    {ordini.map(ordine => (
                      <SelectItem key={ordine.id} value={ordine.id}>
                        {ordine.sigla_provincia} - {ordine.denominazione}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {!isAdminOrdine() && (
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium text-sm mb-3">Ruolo con Accesso (opzionale)</h4>
                <p className="text-xs text-slate-500 mb-3">
                  Seleziona un ruolo solo se l&apos;utente deve poter accedere alla piattaforma
                </p>
                <div className="space-y-2">
                  <Label>Ruolo con Accesso</Label>
                  <Select 
                    value={newUser.ruolo_con_accesso || 'none'} 
                    onValueChange={(v) => setNewUser({ ...newUser, ruolo_con_accesso: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger data-testid="new-user-ruolo-con-accesso">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nessuno (solo ruoli informativi)</SelectItem>
                      {isAmministratore() && (
                        <>
                          <SelectItem value="AMMINISTRATORE">Amministratore</SelectItem>
                          <SelectItem value="ADMIN_ORDINE">Admin Ordine</SelectItem>
                        </>
                      )}
                      <SelectItem value="CONSIGLIERE_C3I">Consigliere C3i</SelectItem>
                      <SelectItem value="COORDINATORE_GTL">Coordinatore GTL</SelectItem>
                    </SelectContent>
                  </Select>
                  {isAdminOrdine() && (
                    <p className="text-xs text-slate-500">
                      Non puoi creare Amministratori o altri Admin Ordine
                    </p>
                  )}
                </div>
              </div>
            )}

            {newUser.ruolo_con_accesso && !isAdminOrdine() && (
              <div className="space-y-2">
                <Label>Password *</Label>
                <Input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder="••••••••"
                  data-testid="new-user-password"
                />
                <p className="text-xs text-slate-500">
                  Obbligatoria per utenti con accesso alla piattaforma
                </p>
              </div>
            )}

            {newUser.ruolo_con_accesso === 'COORDINATORE_GTL' && !isAdminOrdine() && (
              <div className="space-y-2">
                <Label>GTL da Coordinare *</Label>
                <Select 
                  value={newUser.gtl_id} 
                  onValueChange={(v) => setNewUser({ ...newUser, gtl_id: v })}
                >
                  <SelectTrigger data-testid="new-user-gtl">
                    <SelectValue placeholder="Seleziona GTL..." />
                  </SelectTrigger>
                  <SelectContent>
                    {gtlList.filter(g => g.stato === 'attivo').map(gtl => (
                      <SelectItem key={gtl.id} value={gtl.id}>
                        {gtl.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {!isAdminOrdine() && (
              // Amministratore: Multiple checkboxes with mutual exclusivity
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium text-sm mb-3">Ruoli Senza Accesso (opzionali)</h4>
                <p className="text-xs text-slate-500 mb-3">
                  Ruoli informativi/organizzativi (non consentono login)
                </p>
                
                {/* Ruoli GTL - mutuamente esclusivi */}
                <div>
                  <p className="text-xs font-medium text-slate-700 mb-2">Ruoli GTL (mutuamente esclusivi)</p>
                  <div className="space-y-2">
                    {[
                      { value: 'MEMBRO_GTL', label: 'Membro GTL' },
                      { value: 'MEMBRO_SPECIALE_GTL', label: 'Membro Speciale GTL' },
                      { value: 'REFERENTE_GTL', label: 'Referente GTL' }
                    ].map((ruolo) => (
                      <label key={ruolo.value} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={newUser.ruoli_senza_accesso.includes(ruolo.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              // Remove other GTL roles if present
                              const filtered = newUser.ruoli_senza_accesso.filter(r => 
                                r !== 'MEMBRO_GTL' && r !== 'MEMBRO_SPECIALE_GTL' && r !== 'REFERENTE_GTL'
                              );
                              setNewUser({
                                ...newUser,
                                ruoli_senza_accesso: [...filtered, ruolo.value]
                              });
                            } else {
                              setNewUser({
                                ...newUser,
                                ruoli_senza_accesso: newUser.ruoli_senza_accesso.filter(r => r !== ruolo.value)
                              });
                            }
                          }}
                          className="rounded border-slate-300"
                        />
                        <span className="text-sm">{ruolo.label}</span>
                      </label>
                    ))}
                  </div>
                  
                  {/* GTL Multi-Select - Show only if a GTL role is selected */}
                  {newUser.ruoli_senza_accesso.some(r => ['MEMBRO_GTL', 'MEMBRO_SPECIALE_GTL', 'REFERENTE_GTL'].includes(r)) && (
                    <div className="mt-3 p-3 bg-slate-50 rounded border">
                      <p className="text-xs font-medium text-slate-700 mb-2">Seleziona GTL (multipli consentiti)</p>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {gtlList.map((gtl) => (
                          <label key={gtl.id} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={(newUser.gtl_ids || []).includes(gtl.id)}
                              onChange={(e) => {
                                const currentGtlIds = newUser.gtl_ids || [];
                                if (e.target.checked) {
                                  setNewUser({
                                    ...newUser,
                                    gtl_ids: [...currentGtlIds, gtl.id]
                                  });
                                } else {
                                  setNewUser({
                                    ...newUser,
                                    gtl_ids: currentGtlIds.filter(id => id !== gtl.id)
                                  });
                                }
                              }}
                              className="rounded border-slate-300"
                            />
                            <span className="text-xs">{gtl.nome}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateUserModal(false)}>Annulla</Button>
            <Button onClick={handleCreateUser} disabled={saving} data-testid="save-new-user">
              {saving ? 'Creazione...' : 'Crea Utente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={editUserModal} onOpenChange={setEditUserModal}>
        <DialogContent className={isAdminOrdine() ? "max-w-md" : "max-w-2xl"} data-testid="edit-user-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {isAdminOrdine() ? 'Modifica Iscritto' : 'Modifica Utente'}
            </DialogTitle>
          </DialogHeader>
          
          {isAdminOrdine() ? (
            // Admin Ordine: Can only edit email and cellulare
            <div className="space-y-4 py-4">
              {/* Show user name as read-only info */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="font-semibold text-slate-900">
                  {selectedUser?.nome} {selectedUser?.cognome}
                </p>
                {selectedUser?.ruoli_senza_accesso?.includes('DELEGATO_C3I') && (
                  <span className="badge-success mt-2 inline-block">Delegato C3i</span>
                )}
                {selectedUser?.ruoli_senza_accesso?.includes('DELEGATO_SUPPLENTE') && (
                  <span className="badge-warning mt-2 inline-block">Delegato Supplente</span>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={selectedUser?.email || ''}
                  onChange={(e) => setSelectedUser({ ...selectedUser, email: e.target.value })}
                  placeholder="email@esempio.it"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Cellulare</Label>
                <Input
                  type="tel"
                  value={selectedUser?.cellulare || ''}
                  onChange={(e) => setSelectedUser({ ...selectedUser, cellulare: e.target.value })}
                  placeholder="+39 333 1234567"
                />
              </div>
            </div>
          ) : (
            // Amministratore: Full edit capabilities
            <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome *</Label>
                  <Input
                    value={selectedUser?.nome || ''}
                    onChange={(e) => setSelectedUser({ ...selectedUser, nome: e.target.value })}
                    placeholder="Mario"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cognome *</Label>
                  <Input
                    value={selectedUser?.cognome || ''}
                    onChange={(e) => setSelectedUser({ ...selectedUser, cognome: e.target.value })}
                    placeholder="Rossi"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={selectedUser?.email || ''}
                  onChange={(e) => setSelectedUser({ ...selectedUser, email: e.target.value })}
                  placeholder="email@esempio.it"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Cellulare *</Label>
                <Input
                  type="tel"
                  value={selectedUser?.cellulare || ''}
                  onChange={(e) => setSelectedUser({ ...selectedUser, cellulare: e.target.value })}
                  placeholder="+39 333 1234567"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Ordine di Appartenenza *</Label>
                <Select 
                  value={selectedUser?.ordine_appartenenza_id || ''} 
                  onValueChange={(v) => setSelectedUser({ ...selectedUser, ordine_appartenenza_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona ordine..." />
                  </SelectTrigger>
                  <SelectContent>
                    {ordini.map(ordine => (
                      <SelectItem key={ordine.id} value={ordine.id}>
                        {ordine.sigla_provincia} - {ordine.denominazione}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium text-sm mb-3">Ruolo con Accesso</h4>
                <div className="space-y-2">
                  <Label>Ruolo con Accesso</Label>
                  <Select 
                    value={selectedUser?.ruolo_con_accesso || 'none'} 
                    onValueChange={(v) => setSelectedUser({ ...selectedUser, ruolo_con_accesso: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nessuno (solo ruoli informativi)</SelectItem>
                      <SelectItem value="AMMINISTRATORE">Amministratore</SelectItem>
                      <SelectItem value="ADMIN_ORDINE">Admin Ordine</SelectItem>
                      <SelectItem value="CONSIGLIERE_C3I">Consigliere C3i</SelectItem>
                      <SelectItem value="COORDINATORE_GTL">Coordinatore GTL</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Nuova Password (opzionale)</Label>
                <Input
                  type="password"
                  value={selectedUser?.password || ''}
                  onChange={(e) => setSelectedUser({ ...selectedUser, password: e.target.value })}
                  placeholder="Lascia vuoto per non modificare"
                />
                <p className="text-xs text-slate-500">
                  Inserisci solo se vuoi modificare la password
                </p>
              </div>

              {selectedUser?.ruolo_con_accesso === 'COORDINATORE_GTL' && (
                <div className="space-y-2">
                  <Label>GTL da Coordinare *</Label>
                  <Select 
                    value={selectedUser?.gtl_id || ''} 
                    onValueChange={(v) => setSelectedUser({ ...selectedUser, gtl_id: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona GTL..." />
                    </SelectTrigger>
                    <SelectContent>
                      {gtlList.filter(g => g.stato === 'attivo').map(gtl => (
                        <SelectItem key={gtl.id} value={gtl.id}>
                          {gtl.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Amministratore: Multiple checkboxes with mutual exclusivity */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium text-sm mb-3">Ruoli Senza Accesso</h4>
                
                {/* Ruoli GTL - mutuamente esclusivi */}
                <div>
                  <p className="text-xs font-medium text-slate-700 mb-2">Ruoli GTL (mutuamente esclusivi)</p>
                  <div className="space-y-2">
                    {[
                      { value: 'MEMBRO_GTL', label: 'Membro GTL' },
                      { value: 'MEMBRO_SPECIALE_GTL', label: 'Membro Speciale GTL' },
                      { value: 'REFERENTE_GTL', label: 'Referente GTL' }
                    ].map((ruolo) => (
                      <label key={ruolo.value} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={(selectedUser?.ruoli_senza_accesso || []).includes(ruolo.value)}
                          onChange={(e) => {
                            const currentRuoli = selectedUser?.ruoli_senza_accesso || [];
                            if (e.target.checked) {
                              // Remove other GTL roles if present
                              const filtered = currentRuoli.filter(r => 
                                r !== 'MEMBRO_GTL' && r !== 'MEMBRO_SPECIALE_GTL' && r !== 'REFERENTE_GTL'
                              );
                              setSelectedUser({
                                ...selectedUser,
                                ruoli_senza_accesso: [...filtered, ruolo.value]
                              });
                            } else {
                              setSelectedUser({
                                ...selectedUser,
                                ruoli_senza_accesso: currentRuoli.filter(r => r !== ruolo.value)
                              });
                            }
                          }}
                          className="rounded border-slate-300"
                        />
                        <span className="text-sm">{ruolo.label}</span>
                      </label>
                    ))}
                  </div>
                  
                  {/* GTL Multi-Select - Show only if a GTL role is selected */}
                  {(selectedUser?.ruoli_senza_accesso || []).some(r => ['MEMBRO_GTL', 'MEMBRO_SPECIALE_GTL', 'REFERENTE_GTL'].includes(r)) && (
                    <div className="mt-3 p-3 bg-slate-50 rounded border">
                      <p className="text-xs font-medium text-slate-700 mb-2">Seleziona GTL (multipli consentiti)</p>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {gtlList.map((gtl) => (
                          <label key={gtl.id} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={(selectedUser?.gtl_ids || []).includes(gtl.id)}
                              onChange={(e) => {
                                const currentGtlIds = selectedUser?.gtl_ids || [];
                                if (e.target.checked) {
                                  setSelectedUser({
                                    ...selectedUser,
                                    gtl_ids: [...currentGtlIds, gtl.id]
                                  });
                                } else {
                                  setSelectedUser({
                                    ...selectedUser,
                                    gtl_ids: currentGtlIds.filter(id => id !== gtl.id)
                                  });
                                }
                              }}
                              className="rounded border-slate-300"
                            />
                            <span className="text-xs">{gtl.nome}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUserModal(false)}>Annulla</Button>
            <Button onClick={handleEditUser} disabled={saving}>
              {saving ? 'Salvataggio...' : 'Salva Modifiche'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation */}
      <AlertDialog open={deleteUserConfirm} onOpenChange={setDeleteUserConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Conferma Eliminazione</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare l&apos;utente <strong>{selectedUser?.nome} {selectedUser?.cognome}</strong> ({selectedUser?.email})?
              <br /><br />
              Questa azione è irreversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? 'Eliminazione...' : 'Elimina'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Import Modal */}
      <Dialog open={bulkImportModal} onOpenChange={(open) => {
        setBulkImportModal(open);
        if (!open) {
          setCsvData([]);
          setValidationResults(null);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="bulk-import-modal">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Importazione Massiva Utenti
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            {/* Info */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800 font-medium mb-2">Formato CSV richiesto:</p>
              <p className="text-xs text-blue-700 font-mono">
                Nome, Cognome, Email, Cellulare, Ordine, Ruolo con Accesso, Ruoli senza Accesso, Password
              </p>
              <p className="text-xs text-blue-600 mt-2">
                • <strong>Ordine</strong>: Denominazione o Sigla Provincia (es: "Roma" o "RM")<br/>
                • <strong>Ruolo con Accesso</strong>: AMMINISTRATORE, ADMIN_ORDINE, CONSIGLIERE_C3I, COORDINATORE_GTL (opzionale)<br/>
                • <strong>Ruoli senza Accesso</strong>: DELEGATO_C3I, DELEGATO_SUPPLENTE, MEMBRO_GTL, REFERENTE_GTL (separati da virgola)<br/>
                • <strong>Password</strong>: Se non specificata per utenti con accesso, verrà generata come "Cognome2026!"
              </p>
            </div>
            
            {/* Summary */}
            {csvData.length > 0 && (
              <div className="p-4 bg-slate-50 rounded-lg border">
                <p className="font-medium text-slate-700">
                  File caricato: {csvData.length} righe trovate
                </p>
                {validationResults && (
                  <div className="mt-2 flex gap-4">
                    <span className="text-sm text-green-600 flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" />
                      {validationResults.valid} validi
                    </span>
                    {validationResults.errors > 0 && (
                      <span className="text-sm text-red-600 flex items-center gap-1">
                        <XCircle className="w-4 h-4" />
                        {validationResults.errors} errori
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Validation Results Table */}
            {validationResults && (
              <div className="border rounded-lg overflow-hidden">
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left">Riga</th>
                        <th className="px-3 py-2 text-left">Stato</th>
                        <th className="px-3 py-2 text-left">Nome</th>
                        <th className="px-3 py-2 text-left">Cognome</th>
                        <th className="px-3 py-2 text-left">Email</th>
                        <th className="px-3 py-2 text-left">Dettagli</th>
                      </tr>
                    </thead>
                    <tbody>
                      {validationResults.results.map((result, idx) => (
                        <tr key={idx} className={result.status === 'error' ? 'bg-red-50' : 'bg-green-50'}>
                          <td className="px-3 py-2">{result.row}</td>
                          <td className="px-3 py-2">
                            {result.status === 'valid' ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : (
                              <XCircle className="w-4 h-4 text-red-600" />
                            )}
                          </td>
                          <td className="px-3 py-2">{result.nome}</td>
                          <td className="px-3 py-2">{result.cognome}</td>
                          <td className="px-3 py-2">{result.email}</td>
                          <td className="px-3 py-2">
                            {result.errors.length > 0 && (
                              <span className="text-red-600 text-xs">
                                {result.errors.join(', ')}
                              </span>
                            )}
                            {result.warnings.length > 0 && (
                              <span className="text-amber-600 text-xs flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                {result.warnings.join(', ')}
                              </span>
                            )}
                            {result.status === 'valid' && result.errors.length === 0 && result.warnings.length === 0 && (
                              <span className="text-green-600 text-xs">OK</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setBulkImportModal(false)}>
              Annulla
            </Button>
            {!validationResults && (
              <Button onClick={handleValidateCSV} disabled={validating || csvData.length === 0}>
                {validating ? 'Validazione...' : 'Valida Dati'}
              </Button>
            )}
            {validationResults && validationResults.errors === 0 && (
              <Button onClick={handleImportCSV} disabled={importing}>
                {importing ? 'Importazione...' : `Importa ${validationResults.valid} Utenti`}
              </Button>
            )}
            {validationResults && validationResults.errors > 0 && (
              <Button disabled className="bg-red-100 text-red-700">
                Correggi {validationResults.errors} errori prima di importare
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
